import {
  useWSModalCustom,
  WSCard,
  WSFlexBox,
  WSIcon,
  WSInfoBox,
  WSList,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { ChangeEvent, useRef, useState } from "react";
import { WSModalProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSModal/utils";
import { useMutationMemberPrivateCreate } from "../../query/files/mutations";
import { ErrorAlert } from "@components/ErrorAlert";
import { WS_LINKS } from "../../types/wsLinks";

type Props = WSModalProps & {
  onClose: () => void;
};

// Mirrors the allowlist the files service enforces on
// POST /files/member/private/upload (see uploadPolicy.ts there). Kept in the
// UI so the file picker and the disabled Upload button agree with what the
// server will accept; the server remains the control (ARC-1011).
const ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "rtf",
  "odt",
  "csv",
  "tsv",
  "xls",
  "xlsx",
  "ods",
  "ppt",
  "pptx",
  "odp",
  "txt",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "heic",
  "heif",
  "tif",
  "tiff",
  "bmp"
];

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

export const UPLOAD_ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.map(
  extension => `.${extension}`
).join(",");

const getFileExtension = (filename: string) => {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === filename.length - 1) {
    return "";
  }
  return filename.slice(lastDot + 1).toLowerCase();
};

const checkIsFileAllowed = (file: File) => {
  if (!ALLOWED_EXTENSIONS.includes(getFileExtension(file.name))) {
    return false;
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return false;
  }

  return true;
};

const ModalUploadDocumentFile: React.FC<Props> = modalProps => {
  const inputFile = useRef<HTMLInputElement>(null);

  const [fileToUpload, setFileToUpload] = useState<File>();

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (event.target && event.target.files && event.target.files) {
      setFileToUpload(event.target.files[0]);
    }
  };

  const onChooseFile = () => {
    if (inputFile && inputFile.current) {
      inputFile.current.click();
    }
  };

  const isFileAllowed = fileToUpload ? checkIsFileAllowed(fileToUpload) : false;

  const [uploadFile, { error: uploadFileError, isLoading: isUploadingFile }] =
    useMutationMemberPrivateCreate();

  const onFileUpload = async () => {
    if (fileToUpload) {
      const data = new FormData();
      data.append("file", fileToUpload);
      await uploadFile(data, {
        onSuccess: () => {
          modalProps.onClose();
          setFileToUpload(undefined);
        }
      });
    }
  };

  return (
    <WSModal
      title="Upload a file"
      size="S"
      actions={{
        alignment: "fill",
        buttons: [
          {
            label: "Cancel",
            onClick: () => {
              modalProps.onClose?.();
            },
            kind: "Tertiary"
          },
          {
            label: "Upload",
            onClick: onFileUpload,
            visible: !!fileToUpload,
            kind: "Primary",
            disabled: !isFileAllowed,
            loading: isUploadingFile
          },
          {
            label: "Choose a file",
            onClick: onChooseFile,
            kind: "Primary",
            visible: !fileToUpload
          }
        ]
      }}
      {...modalProps}
    >
      <WSList gap="L" p="M">
        <WSInfoBox mb="L">
          <b>Reminder:</b> Your files are encrypted and secured. However,{" "}
          <b>
            “My Documents” is not designed for storing sensitive payment
            information
          </b>
          (such as payment card numbers).{" "}
          <a href={WS_LINKS.security} target="_blank" rel="noopener noreferrer">
            Learn more
          </a>{" "}
          about data security.
        </WSInfoBox>

        <input
          type="file"
          name="file"
          data-testid="fileInput"
          ref={inputFile}
          accept={UPLOAD_ACCEPT_ATTRIBUTE}
          style={{ display: "none" }}
          onChange={onFileChange}
        />
        {fileToUpload ? (
          <>
            <WSCard>
              <WSFlexBox.CenterY gap="M" justify="space-between" wrap="nowrap">
                <WSFlexBox.CenterY gap="XS" wrap="nowrap">
                  <WSIcon name="file" size="M" color="gray500" />
                  <WSText.ParagraphSm color="gray500">
                    {fileToUpload.name}
                  </WSText.ParagraphSm>
                </WSFlexBox.CenterY>

                <WSIcon
                  name="refresh-h"
                  size="M"
                  color="gray500"
                  onClick={onChooseFile}
                />
              </WSFlexBox.CenterY>
            </WSCard>
            <ErrorAlert
              context="UploadFile"
              error={
                isFileAllowed
                  ? uploadFileError
                  : "Sorry! Cannot upload this file. Documents, spreadsheets and images up to 100MB are accepted."
              }
            />
          </>
        ) : null}

        <WSText.ParagraphSm color="gray500">
          Files you upload will be saved to your Documents. Documents,
          spreadsheets and images only, 100MB max file size.
        </WSText.ParagraphSm>
      </WSList>
    </WSModal>
  );
};

export const useModalUploadDocumentFile = () =>
  useWSModalCustom(ModalUploadDocumentFile);
